import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {LinkWrapperControl} from '../../controls/link-wrapper-control/link-wrapper-control';
import {getComponentStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {makeConditionalControl} from '../../controls/conditional-control/conditional-control';
import {ConditionalGatingSource} from '../../controls/conditional-control/conditional-control.types';
import {makeColorControl} from '../../controls/color-control/color-control';
import {textColorDescriptor} from '../../controls/color-control/color-control-descriptors';
import {makeDropdownSelectorControl} from '../../controls/dropdown-selector-control/dropdown-selector-control';
import {widthDropdownDescriptor} from '../../controls/dropdown-selector-control/dropdown-selector-control-descriptors';
import {makeFontSelectorControl} from '../../controls/font-selector-control/font-selector-control';
import {makeOptionListControl} from '../../controls/option-list-control/option-list-control';
import {fontStyleDescriptor, horizontalAlignDescriptor, textAlignDescriptor} from '../../controls/option-list-control/option-list-control-descriptors';
import {makePresetSelectorControl} from '../../controls/preset-selector-control/preset-selector-control';
import {buttonPresetDescriptor} from '../../controls/preset-selector-control/preset-selector-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {
  fontSizeDescriptor,
  letterSpacingDescriptor,
  lineHeightDescriptor,
  opacityDescriptor,
  paddingDescriptor,
  responsiveWidthDescriptor,
} from '../../controls/slider-control/slider-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeCtaButtonControl} from '../../controls/cta-button-control/cta-button-control';
import {copyPasteStylesDescriptor} from '../../controls/cta-button-control/cta-button-control-descriptors';
import {makeAiRewriteControl} from '../../controls/ai-content-control/ai-content-control';
import {registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {deleteControl, duplicateControl, lockControl} from '../shared/common-action-controls';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {bgColorControl} from '../shared/color-controls';
import {isAiTextEnabled} from '../shared/sector-predicates';
import type {Component} from 'grapesjs';
import type {EmailMakerControl} from '../../right-panel-registry.types';

const shapeHeader: EmailMakerControl = makeStyleHeader('pmwjs_shape');
const buttonStylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_button_styles');
const textStylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_text_styles');

const urlControl: EmailMakerControl = LinkWrapperControl;
const presetControl = makePresetSelectorControl(buttonPresetDescriptor);
const copyPasteStylesControl = makeCtaButtonControl(copyPasteStylesDescriptor);
const widthDropdownControl = makeDropdownSelectorControl(widthDropdownDescriptor);
const aiRewriteControl: EmailMakerControl = makeAiRewriteControl();

const BUTTON_WIDTH_PROPERTY = 'button-width';
const BUTTON_WIDTH_AUTO = 'auto' as const;

/** Custom-width slider is hidden when button-width is 'auto' (default when unset). */
function isWidthSliderVisible(component: Component): boolean {
  const buttonWidth = getComponentStyleProperty(component, BUTTON_WIDTH_PROPERTY);
  const effective = buttonWidth !== '' ? buttonWidth : BUTTON_WIDTH_AUTO;
  return effective !== BUTTON_WIDTH_AUTO;
}

const textColorControl = makeColorControl(textColorDescriptor);

const fontControl = makeFontSelectorControl();

const horizontalAlignControl = makeOptionListControl(horizontalAlignDescriptor);
const fontStyleControl = makeOptionListControl(fontStyleDescriptor);
const textAlignControl = makeOptionListControl(textAlignDescriptor);

const opacityControl = makeSliderControl(opacityDescriptor);
const widthControl = makeSliderControl({...responsiveWidthDescriptor, labelKey: 'pmwjs_value'});

const conditionalWidthControl = makeConditionalControl({control: widthControl, predicate: isWidthSliderVisible, gatingSource: ConditionalGatingSource.STYLE, gatingKey: BUTTON_WIDTH_PROPERTY});
const paddingControl = makeSliderControl(paddingDescriptor);
const fontSizeControl = makeSliderControl(fontSizeDescriptor);
const lineHeightControl = makeSliderControl(lineHeightDescriptor);
const letterSpacingControl = makeSliderControl(letterSpacingDescriptor);

registerEmailMakerSector(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON, {
  title: window.i18next.t('pmwjs_button'),
  properties: [
    {control: duplicateControl},
    {control: lockControl},
    {control: deleteControl},
    {control: aiRewriteControl, isVisible: isAiTextEnabled, actionMeta: {icon: 'icon-ai-caption', labelKey: 'pmwjs_ai_rewrite', complexity: TileComplexity.COMPLEX}},
    {control: urlControl, actionMeta: {icon: 'icon-link', labelKey: 'pmwjs_link', complexity: TileComplexity.SIMPLE}},
    {control: shapeHeader},
    {control: presetControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_shape', complexity: TileComplexity.SIMPLE}},
    {control: buttonStylesHeader},
    {control: copyPasteStylesControl},
    {control: opacityControl, tileMeta: {icon: 'icon-opacity', labelKey: 'pmwjs_opacity', complexity: TileComplexity.SIMPLE}},
    {control: bgColorControl, tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_background_color', complexity: TileComplexity.SIMPLE}},
    {control: widthDropdownControl, tileMeta: {icon: 'icon-stretch-horizontal', labelKey: 'pmwjs_width', complexity: TileComplexity.SIMPLE}},
    {control: conditionalWidthControl},
    {control: horizontalAlignControl, tileMeta: {icon: 'icon-h-align-center', labelKey: 'pmwjs_horizontal_alignment', complexity: TileComplexity.SIMPLE}},
    {control: borderStyleControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderColorControl, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderWidthControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_thickness', complexity: TileComplexity.SIMPLE}},
    {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
    {control: textStylesHeader},
    {control: textColorControl, tileMeta: {icon: 'icon-text-fill', labelKey: 'pmwjs_text_color', complexity: TileComplexity.SIMPLE}},
    {control: fontControl, tileMeta: {icon: 'icon-font', labelKey: 'pmwjs_font', complexity: TileComplexity.SIMPLE}},
    {control: fontSizeControl, tileMeta: {icon: 'icon-text-size', labelKey: 'pmwjs_font_size', complexity: TileComplexity.SIMPLE}},
    {control: fontStyleControl, tileMeta: {icon: 'icon-bold', labelKey: 'pmwjs_style', complexity: TileComplexity.SIMPLE}},
    {control: textAlignControl, tileMeta: {icon: 'icon-align-center', labelKey: 'pmwjs_alignment', complexity: TileComplexity.SIMPLE}},
    {control: lineHeightControl, tileMeta: {icon: 'icon-line-height', labelKey: 'pmwjs_line_height', complexity: TileComplexity.SIMPLE}},
    {control: letterSpacingControl, tileMeta: {icon: 'icon-letter-spacing', labelKey: 'pmwjs_letter_spacing', complexity: TileComplexity.SIMPLE}},
  ],
});
